import React from "react";
import PropTypes from "prop-types";

// Material
import { makeStyles } from "@material-ui/core/styles";

// Config
import colors from "../../config/colors";

// State
import { useAppInsights } from "../../utils/telemetry/AppInsights";

// Icons
import videoIcon from "../../assets/icons/video_ico.svg";

// Components
import AddMedia from "../AddMedia";
import AddMedicalRecords from "../AddMedicalRecords";
import EncourageSuspectMediaCard from "../EncourageSuspectMediaCard";
import MediaItem from "../MediaItem";
import MediaReuploadProvider from "./MediaReuploadProvider";
import Text from "../Text";

const useStyles = makeStyles(theme => ({
  placeholderContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(10, 0),
    gap: theme.spacing(2),
    margin: "0 auto"
  },
  placeholderText: {
    textAlign: "center",
    color: colors.riverBed,
    opacity: "0.4",
    marginBottom: theme.spacing(1),
    lineHeight: 1.5
  },
  hidden: {
    display: "none"
  }
}));

const buttonComponent = {
  medicalRecordsPage: AddMedicalRecords,
  mediaPage: AddMedia
};

const MediaItemList = ({
  disablePlaceholder,
  displayEncourage,
  data,
  caseId,
  mediaItemProps,
  page,
  onDelete,
  onSelect,
  onFetchImage,
  noUpload
}) => {
  const { trackException } = useAppInsights();
  const classes = useStyles();
  const showPlaceholder = !disablePlaceholder && data.length === 0;
  const Button = buttonComponent[page] || null;

  const handleDelete = itemId => () => {
    if (onDelete) {
      onDelete({
        caseId,
        itemId,
        onFail: () =>
          trackException(
            `Failed to delete ${page} item: ${itemId} at case: ${caseId}`
          )
      });
    }
  };

  const mappedItems = data.map(item => (
    <MediaItem
      data={item}
      handleFetchImage={onFetchImage(item.id)}
      key={item.id}
      onClick={onSelect(item.id)}
      onDelete={handleDelete(item.id)}
      {...mediaItemProps}
    />
  ));

  return (
    <>
      {!disablePlaceholder && (
        <div
          className={
            showPlaceholder ? classes.placeholderContainer : classes.hidden
          }
        >
          {showPlaceholder && (
            <>
              <img src={videoIcon} alt="" />
              <Text
                className={classes.placeholderText}
                text={`${page}.emptyPlaceholder`}
                type="subtitle1Big"
              />
            </>
          )}
          {Button && (
            <Button caseId={caseId} text={`${page}.addMediaContentButton`} />
          )}
        </div>
      )}
      {!showPlaceholder && (
        <MediaReuploadProvider noUpload={noUpload} page={page} caseId={caseId}>
          {mappedItems}
          {displayEncourage && <EncourageSuspectMediaCard />}
        </MediaReuploadProvider>
      )}
    </>
  );
};

MediaItemList.propTypes = {
  caseId: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  mediaItemProps: PropTypes.shape({}).isRequired,
  onDelete: PropTypes.func,
  onFetchImage: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  page: PropTypes.string.isRequired,
  disablePlaceholder: PropTypes.bool,
  displayEncourage: PropTypes.bool,
  noUpload: PropTypes.bool
};

MediaItemList.defaultProps = {
  disablePlaceholder: false,
  displayEncourage: false,
  noUpload: false,
  onDelete: null
};

export default React.memo(MediaItemList);
